import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useLogout, useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { AlertTriangle, Fingerprint, KeyRound, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";

import { authenticateWithPasskey } from "@/lib/webauthn";

interface TwoFactorSearch {
  next?: string;
}

const DEFAULT_RETURN = "/dashboard";

export const Route = createFileRoute("/_public/2fa-challenge")({
  validateSearch: (search: Record<string, unknown>): TwoFactorSearch => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: TwoFactorChallengePage,
});

function TwoFactorChallengePage() {
  const { next } = Route.useSearch();
  const { status } = useSession();
  const callbackUrl = next ?? DEFAULT_RETURN;

  if (status === "unauthenticated") {
    return <Navigate to="/login" search={{ next: `/2fa-challenge` }} replace />;
  }

  return (
    <main className="bg-muted/30 flex min-h-dvh items-center justify-center p-4">
      <div className="bg-card border-border w-full max-w-md rounded-2xl border p-6 shadow-sm sm:p-8">
        <div className="text-center">
          <div className="bg-primary/10 text-primary mx-auto mb-3 flex size-10 items-center justify-center rounded-full">
            <ShieldCheck className="size-5" />
          </div>
          <h1 className="text-foreground text-xl font-semibold">
            <Trans>Two-factor verification</Trans>
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            <Trans>
              Admin actions require a recent 2FA step-up. Use a passkey or enter a 6-digit code from
              your authenticator app.
            </Trans>
          </p>
        </div>
        <ChallengeForm callbackUrl={callbackUrl} />
      </div>
    </main>
  );
}

type Mode = "totp" | "recovery";

interface TwoFactorStatus {
  passkeyAvailable: boolean;
}

function ChallengeForm({ callbackUrl }: { callbackUrl: string }) {
  const { t } = useLingui();
  const logout = useLogout();
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Probe whether the current account has any registered passkeys.
  useEffect(() => {
    let cancelled = false;
    apiFetch
      .get<TwoFactorStatus>("/api/account/2fa/status")
      .then((data) => {
        if (!cancelled) setPasskeyAvailable(!!data.passkeyAvailable);
      })
      .catch(() => {
        if (!cancelled) setPasskeyAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [mode]);

  const submit = useCallback(async () => {
    const sanitized =
      mode === "totp" ? code.replace(/\s/g, "") : code.replace(/\s/g, "").toLowerCase();
    if (mode === "totp" && sanitized.length !== 6) return;
    if (mode === "recovery" && sanitized.replace(/-/g, "").length < 8) return;

    setLoading(true);
    setError(null);
    try {
      const path = mode === "totp" ? "/api/auth/2fa/challenge" : "/api/auth/2fa/challenge/recovery";
      await apiFetch.post(path, { code: sanitized });
      if (typeof window !== "undefined") window.location.assign(callbackUrl);
    } catch (err: unknown) {
      // Wrong codes escalate to bans (5 min → 24 h) shared by every route that
      // checks the code; a code already accepted once is refused, not counted.
      const errorCode =
        err instanceof ApiError && typeof err.body === "object" && err.body !== null
          ? (err.body as { error?: unknown }).error
          : undefined;
      if (err instanceof ApiError && err.status === 429) {
        setError(t`Too many wrong codes. For your security, wait a few minutes and try again.`);
      } else if (errorCode === "code_already_used") {
        setError(t`This code was just used — wait for the next one.`);
      } else {
        setError(t`Invalid code. Try again.`);
      }
      setCode("");
    } finally {
      setLoading(false);
    }
  }, [code, mode, callbackUrl, t]);

  const handlePasskey = useCallback(async () => {
    setPasskeyBusy(true);
    setPasskeyError(null);
    try {
      const ok = await authenticateWithPasskey("challenge");
      if (ok && typeof window !== "undefined") {
        window.location.assign(callbackUrl);
        return;
      }
      // Cancelled — silently fall back to TOTP form
      setPasskeyBusy(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setPasskeyError(t`Too many attempts. Try again in a minute.`);
      } else {
        setPasskeyError(t`Passkey verification failed. Use a code instead.`);
      }
      setPasskeyBusy(false);
    }
  }, [callbackUrl, t]);

  const clean = code.replace(/\s/g, "");

  return (
    <div className="mt-6">
      {passkeyAvailable && (
        <div className="mb-4 flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePasskey}
            disabled={passkeyBusy}
            className="min-h-[48px] w-full gap-1.5"
          >
            {passkeyBusy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Fingerprint className="size-4" aria-hidden />
            )}
            <Trans>Use a passkey</Trans>
          </Button>
          {passkeyError && (
            <p className="text-destructive flex items-center gap-1.5 text-xs" role="alert">
              <AlertTriangle className="size-3.5" aria-hidden />
              {passkeyError}
            </p>
          )}
          <div className="text-muted-foreground flex items-center gap-2 text-[0.6875rem] tracking-wide uppercase">
            <span className="bg-border h-px flex-1" />
            <span>
              <Trans>or</Trans>
            </span>
            <span className="bg-border h-px flex-1" />
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {mode === "totp" ? (
          <m.form
            key="totp"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-3"
          >
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className={cn(
                "border-border bg-background text-foreground placeholder:text-muted-foreground h-14 w-full rounded-lg border text-center font-mono text-2xl tracking-[0.5em] transition-colors outline-none",
                "focus:border-primary focus:ring-primary/20 focus:ring-2",
                error && "border-destructive focus:border-destructive focus:ring-destructive/20",
              )}
            />
            {error && (
              <p className="text-destructive flex items-center gap-1.5 text-sm" role="alert">
                <AlertTriangle className="size-4" aria-hidden />
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={clean.length !== 6 || loading}
              className="min-h-[48px] w-full"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <>
                  <ShieldCheck className="size-4" aria-hidden />
                  <span className="ml-1.5">
                    <Trans>Confirm</Trans>
                  </span>
                </>
              )}
            </Button>
          </m.form>
        ) : (
          <m.form
            key="recovery"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-3"
          >
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.slice(0, 15))}
              placeholder="xxxxx-xxxxx"
              className={cn(
                "border-border bg-background text-foreground placeholder:text-muted-foreground h-14 w-full rounded-lg border text-center font-mono text-lg tracking-[0.25em] transition-colors outline-none",
                "focus:border-primary focus:ring-primary/20 focus:ring-2",
                error && "border-destructive focus:border-destructive focus:ring-destructive/20",
              )}
            />
            {error && (
              <p className="text-destructive flex items-center gap-1.5 text-sm" role="alert">
                <AlertTriangle className="size-4" aria-hidden />
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={clean.replace(/-/g, "").length < 8 || loading}
              className="min-h-[48px] w-full"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <>
                  <KeyRound className="size-4" aria-hidden />
                  <span className="ml-1.5">
                    <Trans>Use recovery code</Trans>
                  </span>
                </>
              )}
            </Button>
          </m.form>
        )}
      </AnimatePresence>

      <div className="mt-4 flex flex-col items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => {
            setMode(mode === "totp" ? "recovery" : "totp");
            setCode("");
            setError(null);
          }}
          className="text-primary hover:underline"
        >
          {mode === "totp" ? (
            <Trans>Use a recovery code instead</Trans>
          ) : (
            <Trans>Back to authenticator code</Trans>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            void logout.logout();
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          <Trans>Sign out</Trans>
        </button>
      </div>
    </div>
  );
}
